import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FileText, Lightbulb, Plus, Clock, Trash2, Loader, ArrowUpRight, X, Send, Loader2, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";
const PRIORITY_STYLES = {
  high: { bg: "#FF2A0015", text: "#FF2A00" },
  medium: { bg: "#FF8C0015", text: "#FF8C00" },
  low: { bg: "#018D4C15", text: PRIMARY },
};

function OutlineCard({ outline, onDelete }) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm("Delete this draft?")) return;
    setDeleting(true);
    try {
      await api.deleteOutline(outline.id);
      onDelete(outline.id);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/dashboard/writing/${outline.id}`)}
      className="bg-white rounded-xl border border-zinc-100 p-5 hover:shadow-md transition-all shadow-sm cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-zinc-900 leading-tight mb-2 line-clamp-2">
            {outline.title || "Untitled"}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {outline.cluster && (
              <span className="text-[10px] text-zinc-400">{outline.cluster}</span>
            )}
            {outline.cluster && <span className="text-[10px] text-zinc-300">·</span>}
            <span className="flex items-center gap-1 text-[10px] text-zinc-400">
              <Clock size={9} />
              {new Date(outline.updated_at).toLocaleDateString()}
            </span>
            {outline.wp_draft_link && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wide"
                style={{ background: "#018D4C15", color: PRIMARY }}
              >
                DRAFT
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex-shrink-0 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 text-zinc-300 rounded-lg transition-all"
        >
          {deleting ? (
            <Loader size={13} className="animate-spin" />
          ) : (
            <Trash2 size={13} />
          )}
        </button>
      </div>
    </div>
  );
}

async function pollOutlineReady(id) {
  const MAX_POLLS = 60;
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const { status, error } = await api.getOutlineStatus(id);
    if (status === "done") return;
    if (status === "error") throw new Error(error || "Outline generation failed");
  }
  throw new Error("Outline generation timed out after 2 minutes");
}

function IdeaCard({ item }) {
  const style = PRIORITY_STYLES[item.priority?.toLowerCase()] || PRIORITY_STYLES.medium;
  const [approveState, setApproveState] = useState("idle");
  const [wpLink, setWpLink] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [outlineId, setOutlineId] = useState(null);
  const [wpPostStatus, setWpPostStatus] = useState(null);
  const [checkingWpStatus, setCheckingWpStatus] = useState(false);

  const approve = async () => {
    setApproveState("generating");
    setApproveError(null);
    try {
      const outline = await api.generateOutline({
        title: item.title,
        cluster: item.cluster || null,
        reason: item.reason || null,
      });
      if (outline.status === "generating") {
        await pollOutlineReady(outline.id);
      }
      setApproveState("pushing");
      const result = await api.pushOutlineToWp(outline.id);
      if (result.success) {
        setOutlineId(outline.id);
        setWpLink(result.link || result.edit_link || null);
        setApproveState("done");
      } else {
        throw new Error(result.detail || "Push failed");
      }
    } catch (e) {
      console.error("[approve] failed", e);
      setApproveError(e?.response?.data?.detail || e.message || "Failed");
      setApproveState("error");
    }
  };

  const checkWpStatus = async () => {
    if (!outlineId || checkingWpStatus) return;
    setCheckingWpStatus(true);
    try {
      const res = await api.getOutlineWpStatus(outlineId);
      setWpPostStatus(res.status || null);
      if (res.link) setWpLink(res.link);
    } catch (e) {
      console.error("[wp-status] check failed", e);
    } finally {
      setCheckingWpStatus(false);
    }
  };

  const renderApprove = () => {
    if (approveState === "idle") return (
      <button
        onClick={approve}
        title="Generate outline & push draft"
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wide rounded-lg transition-colors hover:opacity-90 text-white"
        style={{ backgroundColor: PRIMARY }}
      >
        <Send size={10} strokeWidth={2.5} />
        APPROVE
      </button>
    );
    if (approveState === "generating") return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold">
        <Loader2 size={11} className="animate-spin" />
        Outlining…
      </span>
    );
    if (approveState === "pushing") return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold">
        <Loader2 size={11} className="animate-spin" />
        Pushing…
      </span>
    );
    if (approveState === "done") {
      const isPublished = wpPostStatus === "publish";
      const label = isPublished ? "Published" : "Draft live";
      const color = isPublished ? "text-emerald-700" : "text-green-600";
      return (
        <span className="flex items-center gap-1.5 text-[10px] font-bold">
          <CheckCircle size={11} strokeWidth={2.5} className={color} />
          {wpLink ? (
            <a href={wpLink} target="_blank" rel="noopener noreferrer" className={`underline underline-offset-2 ${color}`}>
              {label}
            </a>
          ) : <span className={color}>{label}</span>}
          {outlineId && (
            <button
              onClick={checkWpStatus}
              title="Check current draft status"
              className="text-zinc-400 hover:text-zinc-600 transition-colors"
            >
              {checkingWpStatus
                ? <Loader2 size={10} className="animate-spin" />
                : <RefreshCw size={10} strokeWidth={2} />}
            </button>
          )}
        </span>
      );
    }
    if (approveState === "error") return (
      <button
        onClick={approve}
        title={approveError}
        className="flex items-center gap-1 text-[10px] font-bold text-red-500 hover:text-red-700"
      >
        <AlertCircle size={11} />
        Retry
      </button>
    );
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm flex flex-col">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase rounded-full"
            style={{ background: style.bg, color: style.text }}
          >
            {item.priority || "medium"}
          </span>
          {item.cluster && (
            <span className="text-[10px] text-zinc-400">{item.cluster}</span>
          )}
        </div>
        <h3 className="text-sm font-bold text-zinc-900 leading-tight mb-1.5">{item.title}</h3>
        {item.reason && (
          <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">{item.reason}</p>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-50 flex items-center justify-between">
        {renderApprove()}
        {approveState === "error" && approveError && (
          <span className="text-[9px] text-red-400 truncate max-w-[140px]" title={approveError}>{approveError}</span>
        )}
      </div>
    </div>
  );
}

function NewOutlineModal({ onClose, onCreate }) {
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      await onCreate({ title: title.trim() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border border-zinc-100 shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-zinc-900">New Outline</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 transition-colors">
            <X size={15} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500 mb-1.5 block">
              Article Title
            </label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. How to Optimize Landing Pages for SEO"
              className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-400 text-zinc-900"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || loading}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
              style={{ backgroundColor: PRIMARY }}
            >
              {loading ? <Loader size={12} className="animate-spin" /> : <Plus size={12} strokeWidth={2.5} />}
              {loading ? "Generating..." : "Generate Outline"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function IdeaGroups({ suggestions }) {
  const groups = [];
  const seen = new Map();

  for (const item of suggestions) {
    const key = item.cluster || "";
    if (!seen.has(key)) {
      seen.set(key, { label: item.cluster || "Other Ideas", size: item.cluster_size ?? null, items: [] });
      groups.push(seen.get(key));
    }
    seen.get(key).items.push(item);
  }

  // Thin clusters (known size) first, then null-size groups, all sorted ascending
  groups.sort((a, b) => {
    if (a.size === null && b.size === null) return 0;
    if (a.size === null) return 1;
    if (b.size === null) return -1;
    return a.size - b.size;
  });

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
              Strengthen: {g.label}
            </span>
            {g.size !== null && (
              <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-zinc-100 text-zinc-400">
                {g.size} article{g.size !== 1 ? "s" : ""} currently
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {g.items.map((item, i) => (
              <IdeaCard
                key={i}
                item={item}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Writing() {
  const navigate = useNavigate();
  const [outlines, setOutlines] = useState([]);
  const [recData, setRecData] = useState({ suggestions: [], status: "not_generated" });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    Promise.all([api.listOutlines(), api.getRecommendations()])
      .then(([outlineRes, recs]) => {
        setOutlines(outlineRes.outlines || []);
        setRecData(recs);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleNewOutline = async ({ title }) => {
    setShowModal(false);
    try {
      const outline = await api.generateOutline({ title });
      navigate(`/dashboard/writing/${outline.id}`);
    } catch (e) {
      console.error(e);
      alert("Failed to generate outline: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = (id) => {
    setOutlines((prev) => prev.filter((o) => o.id !== id));
  };

  const hasIdeas = recData.suggestions?.length > 0;
  const hasOutlines = outlines.length > 0;

  return (
    <div data-testid="writing-page" className="p-6 sm:p-8 max-w-5xl mx-auto">
      {showModal && (
        <NewOutlineModal onClose={() => setShowModal(false)} onCreate={handleNewOutline} />
      )}

      <div className="mb-8 pb-6 border-b border-zinc-100 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">
            Content Studio
          </p>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">Writing</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Turn ideas into structured outlines, then into full articles
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold tracking-wide text-white rounded-lg hover:opacity-90 transition-all"
          style={{ backgroundColor: PRIMARY }}
        >
          <Plus size={14} strokeWidth={2.5} />
          NEW OUTLINE
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-8">Loading...</div>
      ) : (
        <div className="space-y-10">
          {hasOutlines && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <FileText size={14} className="text-zinc-400" />
                <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
                  Drafts ({outlines.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {outlines.map((o) => (
                  <OutlineCard key={o.id} outline={o} onDelete={handleDelete} />
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lightbulb size={14} className="text-zinc-400" />
                <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
                  Ideas {hasIdeas ? `(${recData.suggestions.length})` : ""}
                </h2>
              </div>
              <Link
                to="/dashboard/recommendations"
                className="text-[10px] text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                Generate more →
              </Link>
            </div>

            {!hasIdeas ? (
              <div className="bg-white rounded-xl border border-zinc-100 p-12 text-center shadow-sm">
                <Lightbulb size={36} color="#CCC" className="mx-auto" />
                <p className="text-sm font-bold text-zinc-900 mt-4">No ideas yet</p>
                <p className="text-xs text-zinc-500 mt-1 mb-5">
                  Generate recommendations to get article ideas.
                </p>
                <Link
                  to="/dashboard/recommendations"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <ArrowUpRight size={13} />
                  Generate Ideas
                </Link>
              </div>
            ) : (
              <IdeaGroups
                suggestions={recData.suggestions}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
